import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, FileText, RotateCcw, Loader2, Check, Users, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/axiosClient";
import { useAuth } from "../context/AuthContext";
import { FeedbackCallout } from "../components/Feedback";

const QuadrantGrid = ({ x, y, quadrantKey, isMixed }) => {
  // x: emotional connection 0-100 (left -> right)
  // y: collaborative 0-100 (bottom -> top)
  const clampedX = Math.max(2, Math.min(98, x));
  const clampedY = Math.max(2, Math.min(98, y));

  const cells = [
    { k: "stable",   label: "Stable but\nDisconnected",  bg: "#F7F5F0", pos: "top-left" },
    { k: "thriving", label: "Collaborative\n& Thriving", bg: "#EBF2EA", pos: "top-right" },
    { k: "strained", label: "Strained &\nAdversarial",   bg: "#F3F4F6", pos: "bottom-left" },
    { k: "caring",   label: "Caring but\nReactive",      bg: "#FDF5EE", pos: "bottom-right" },
  ];

  return (
    <div className="w-full max-w-md mx-auto">
      {/* y-axis top label */}
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold mb-3">
        Higher collaboration ↑
      </div>

      <div className="relative flex">
        {/* y-axis vertical label */}
        <div className="flex items-center justify-center w-6">
          <span className="-rotate-90 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold">
            Collaborative Conflict
          </span>
        </div>

        <div
          className="relative flex-1 aspect-square border-2 border-[#E8E2D9] rounded-2xl overflow-hidden grid grid-cols-2 grid-rows-2 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          data-testid="results-quadrant-grid"
        >
          {cells.map((c) => (
            <div
              key={c.k}
              style={{ background: c.bg }}
              className={[
                "relative flex items-center justify-center p-3 text-center transition-all",
                !isMixed && quadrantKey === c.k ? "ring-2 ring-inset ring-[#C86B5E]/70" : "",
              ].join(" ")}
              data-testid={`quadrant-cell-${c.k}`}
            >
              <span
                className={[
                  "text-[11px] sm:text-xs font-semibold uppercase tracking-wider leading-snug whitespace-pre-line",
                  !isMixed && quadrantKey === c.k ? "text-[#2D2A26]" : "text-[#8A8782]",
                ].join(" ")}
              >
                {c.label}
              </span>
            </div>
          ))}

          {/* dividing lines */}
          <div className="absolute top-1/2 left-0 right-0 h-px bg-[#D1C7B8]/70 pointer-events-none" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#D1C7B8]/70 pointer-events-none" />

          {/* Mixed Patterns soft zone in middle */}
          <div
            className={[
              "absolute pointer-events-none rounded-full transition-all",
              isMixed ? "ring-2 ring-[#C86B5E]/40" : "",
            ].join(" ")}
            style={{
              left: "25%",
              top: "25%",
              width: "50%",
              height: "50%",
              background:
                "radial-gradient(circle, rgba(253,251,247,0.92) 0%, rgba(253,251,247,0.65) 55%, rgba(253,251,247,0) 80%)",
            }}
            data-testid="mixed-zone-circle"
          />
          <div
            className="absolute pointer-events-none top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center"
            data-testid="mixed-zone-label"
          >
            <p
              className={[
                "text-[10px] uppercase tracking-[0.18em] font-semibold leading-tight",
                isMixed ? "text-[#C86B5E]" : "text-[#8A8782]",
              ].join(" ")}
            >
              Mixed
              <br />
              Patterns
            </p>
          </div>

          {/* point marker */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 220, damping: 18 }}
            className="absolute z-20 point-pulse"
            style={{
              left: `${clampedX}%`,
              bottom: `${clampedY}%`,
              transform: "translate(-50%, 50%)",
            }}
            data-testid="user-point-marker"
          >
            <div className="w-5 h-5 rounded-full bg-[#C86B5E] border-[3px] border-white shadow-md" />
          </motion.div>
        </div>
      </div>

      {/* x-axis label */}
      <div className="flex justify-between mt-3 ml-6 text-[10px] uppercase tracking-[0.2em] text-[#5C5A56] font-semibold">
        <span>← Lower connection</span>
        <span>Higher connection →</span>
      </div>
      <div className="text-center text-[11px] uppercase tracking-[0.22em] text-[#2D2A26] font-semibold mt-2">
        Emotional Connection
      </div>
    </div>
  );
};

const Bar = ({ name, pct, testId }) => (
  <div data-testid={testId}>
    <div className="flex items-baseline justify-between mb-2">
      <span className="text-sm sm:text-base text-[#2D2A26] font-medium">{name}</span>
      <span className="text-sm font-semibold text-[#C86B5E]">{Math.round(pct)}%</span>
    </div>
    <div className="w-full h-1.5 bg-[#E8E2D9] rounded-full overflow-hidden">
      <motion.div
        className="h-full bg-[#C86B5E] rounded-full"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      />
    </div>
  </div>
);

const Results = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Invite-partner state
  const [yourName, setYourName] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/submissions/${id}`);
        if (active) setData(res.data);
      } catch (e) {
        console.error(e);
        if (active) toast.error(formatApiError(e, "Could not load your results."));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/submissions/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `relationship-report-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't download the PDF."));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const createCoupleInvite = async () => {
    setCreatingInvite(true);
    try {
      const { data: couple } = await api.post("/couples", {
        submission_id: id,
        your_label: yourName.trim() || (user?.name || "You"),
        partner_label: partnerName.trim() || "Partner",
      });
      const url = `${window.location.origin}/couple/${couple.id}`;
      setInviteUrl(url);
      toast.success("Invite link ready — share it with your partner.");
    } catch (e) {
      console.error(e);
      toast.error(formatApiError(e, "Couldn't create the invite."));
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyInvite = () => {
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Link copied");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#C86B5E]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#5C5A56] mb-4">We couldn’t find these results.</p>
          <Link to="/" className="text-[#C86B5E] underline">Start again</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="results-page">
      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-full bg-[#F8EBE9] flex items-center justify-center">
              <Heart className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.8} />
            </div>
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
              Your reflection
            </span>
          </div>

          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#2D2A26] leading-tight font-semibold">
            Your main pattern is{" "}
            <span className="text-[#C86B5E]" data-testid="pattern-label">
              {data.pattern_label}
            </span>.
          </h1>
          <p
            className="mt-3 text-sm sm:text-base text-[#5C5A56] leading-relaxed max-w-2xl"
            data-testid="match-phrase"
          >
            {data.match_phrase}
          </p>
        </motion.div>

        {/* Grid + meta scores */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 mt-12 items-start">
          <div>
            <QuadrantGrid
              x={data.emotional_connection_pct}
              y={data.collaborative_conflict_pct}
              quadrantKey={data.quadrant_key}
              isMixed={data.classification === "mixed"}
            />
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl p-5 bg-[#FDF5EE]" data-testid="meta-emotional-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
                  Emotional Connection
                </p>
                <p className="font-serif-display text-3xl sm:text-4xl text-[#2D2A26] mt-2 font-semibold">
                  {Math.round(data.emotional_connection_pct)}%
                </p>
              </div>
              <div className="rounded-2xl p-5 bg-[#EBF2EA]" data-testid="meta-collaborative-card">
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
                  Collaborative Conflict
                </p>
                <p className="font-serif-display text-3xl sm:text-4xl text-[#2D2A26] mt-2 font-semibold">
                  {Math.round(data.collaborative_conflict_pct)}%
                </p>
              </div>
            </div>

            <div
              className="rounded-2xl bg-white border border-[#E8E2D9] p-6 sm:p-7 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
              data-testid="interpretation-card"
            >
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8782] font-semibold mb-3">
                What this suggests
              </p>
              <p className="text-[15px] sm:text-base text-[#2D2A26] leading-relaxed">
                {data.interpretation}
              </p>
            </div>

            {data.growth_direction && (
              <div
                className="rounded-2xl p-6 sm:p-7 border border-[#E8E2D9]"
                style={{ background: "#F8EBE9" }}
                data-testid="growth-direction-card"
              >
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#C86B5E] font-semibold mb-3">
                  Growth direction
                </p>
                <p className="text-[15px] sm:text-base text-[#2D2A26] leading-relaxed">
                  {data.growth_direction}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Dimensions */}
        <div className="mt-16">
          <h2 className="font-serif-display text-2xl sm:text-3xl text-[#2D2A26] font-medium">
            Six dimensions of your relationship
          </h2>
          <p className="text-sm text-[#8A8782] mt-1">Each scored from 0% to 100%.</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-6" data-testid="dimensions-list">
            {data.dimensions.map((d) => (
              <Bar
                key={d.key}
                name={d.name}
                pct={d.percentage}
                testId={`dimension-score-bar-${d.key}`}
              />
            ))}
          </div>
        </div>

        {/* Invite partner — Couple's Mode */}
        <div
          className="mt-16 rounded-2xl p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          style={{ background: "#FDF5EE", border: "1px solid #E8E2D9" }}
          data-testid="invite-partner-panel"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 border border-[#E8E2D9]">
              <Users className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-serif-display text-xl text-[#2D2A26] font-medium">
                Invite your partner to compare
              </h3>
              <p className="text-sm text-[#5C5A56] mt-1">
                Send them a private link. When they finish, you’ll both see your points on the same map.
              </p>
            </div>
          </div>

          {!inviteUrl ? (
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <input
                type="text"
                placeholder="Your name (e.g. Alex)"
                value={yourName}
                onChange={(e) => setYourName(e.target.value)}
                data-testid="your-name-input"
                maxLength={40}
                className="px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] placeholder-[#8A8782] focus:outline-none focus:border-[#C86B5E] transition-colors"
              />
              <input
                type="text"
                placeholder="Partner's name (e.g. Sam)"
                value={partnerName}
                onChange={(e) => setPartnerName(e.target.value)}
                data-testid="partner-name-input"
                maxLength={40}
                className="px-5 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] placeholder-[#8A8782] focus:outline-none focus:border-[#C86B5E] transition-colors"
              />
              <button
                onClick={createCoupleInvite}
                disabled={creatingInvite}
                data-testid="create-invite-button"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3 font-medium hover:bg-[#B35B4E] disabled:opacity-50 transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                {creatingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                Create invite link
              </button>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  readOnly
                  value={inviteUrl}
                  data-testid="invite-url-input"
                  className="flex-1 px-4 py-3 rounded-full border-2 border-[#E8E2D9] bg-white text-sm text-[#5C5A56]"
                />
                <button
                  onClick={copyInvite}
                  data-testid="copy-invite-button"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-[#E8E2D9] hover:border-[#D1C7B8] text-sm font-medium bg-white text-[#2D2A26] transition-all"
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
                <button
                  onClick={() => navigate(inviteUrl.replace(window.location.origin, ""))}
                  data-testid="view-couple-page-button"
                  className="inline-flex items-center justify-center gap-2 bg-[#2D2A26] text-white rounded-full px-5 py-3 font-medium text-sm hover:bg-[#1f1d1a] transition-all"
                >
                  Open
                </button>
              </div>
              <p className="text-xs text-[#8A8782]">
                Share this link with your partner. Check back here to see your combined results once they finish.
              </p>
            </div>
          )}
        </div>

        {/* Report — auto-emailed + PDF download */}
        <div
          className="mt-8 rounded-2xl bg-white border border-[#E8E2D9] p-6 sm:p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
          data-testid="report-panel"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-[#F8EBE9] flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-serif-display text-xl text-[#2D2A26] font-medium">
                  Your report is ready
                </h3>
                {data.email ? (
                  <p
                    className="text-sm text-[#5C5A56] mt-1 inline-flex items-center gap-1.5"
                    data-testid="auto-emailed-indicator"
                  >
                    <Check className="w-3.5 h-3.5 text-[#8A9A86]" strokeWidth={2.5} />
                    Emailed to <span className="text-[#2D2A26] font-semibold">{data.email}</span>
                  </p>
                ) : (
                  <p className="text-sm text-[#5C5A56] mt-1">
                    Download a PDF copy for your records.
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-button"
              className="inline-flex items-center justify-center gap-2 bg-[#2D2A26] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1f1d1a] disabled:opacity-50 transition-all"
            >
              {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Download PDF
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link
            to="/quiz"
            data-testid="retake-quiz-button"
            className="inline-flex items-center gap-2 text-[#2D2A26] border-2 border-[#E8E2D9] hover:border-[#D1C7B8] rounded-full px-6 py-3 font-medium text-sm bg-white transition-all"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={2} />
            Retake the quiz
          </Link>
          <Link to="/" className="text-sm text-[#8A8782] hover:text-[#2D2A26] transition-colors">
            ← Back to start
          </Link>
        </div>

        <FeedbackCallout />

        <p
          className="mt-16 text-xs text-[#8A8782] leading-relaxed text-center max-w-2xl mx-auto"
          data-testid="disclaimer-text"
        >
          This quiz is for reflection and education only. It is not a clinical diagnosis or a
          substitute for counseling, therapy, or professional advice.
        </p>
      </div>
    </div>
  );
};

export default Results;
